import {useEffect, useState} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import LoadingIndicator from '../../../components/loading-indicator';
import {AppContent, ContentRow} from '../../../styled/components';
import {ActionsRow, ContentRowHeader, FormRow, ProvinceContainer} from './province-form.styles';
import {getCountriesForSelect, getProvince, saveProvince, updateProvince} from '../api-service';

const emptyProvince = {
  name: '',
  code: '',
  countryId: null,
  countryName: '',
};

function ProvinceForm({provinceId, onCancel, onSaved}) {
  const isEditMode = provinceId !== null && provinceId !== 'new';
  const [formData, setFormData] = useState(emptyProvince);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(null);

  const title = isEditMode ? `States / ${formData?.name || ''}` : 'States / New';
  const isFormValid = formData.name?.trim().length > 0 && Boolean(formData.countryId);

  useEffect(() => {
    getCountriesForSelect()
      .then((result) => setCountries(result.data || []))
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      setFormData(emptyProvince);
      return;
    }
    setLoading(true);
    getProvince(provinceId)
      .then((province) => {
        if (!province) {
          setServerError(`State ${provinceId} not found`);
          return;
        }
        setFormData({
          ...emptyProvince,
          ...province,
          code: province.code || '',
          countryName: province.countryName || '',
        });
      })
      .catch((err) => setServerError(err.message || 'Error loading state'))
      .finally(() => setLoading(false));
  }, [provinceId, isEditMode]);

  const handleFieldChange = (ev) => {
    const {name, value} = ev.target;
    setFormData((prev) => ({...prev, [name]: value}));
  };

  const handleCountryChange = (_event, country) => {
    setFormData((prev) => ({
      ...prev,
      countryId: country?.id ?? null,
      countryName: country?.name ?? '',
    }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!isFormValid) return;
    setLoading(true);
    setServerError(null);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code?.trim() || null,
        countryId: Number(formData.countryId),
      };
      if (isEditMode) {
        const updated = await updateProvince(provinceId, payload);
        onSaved?.(updated ?? {id: provinceId});
      } else {
        const created = await saveProvince(payload);
        onSaved?.(created);
      }
    } catch (err) {
      setServerError(err.message || 'Error saving state');
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry = countries.find((c) => c.id === formData.countryId)
    || (formData.countryId ? {id: formData.countryId, name: formData.countryName || ''} : null);

  return (
    <>
      {loading && <LoadingIndicator/>}
      <AppContent>
        <ContentRow sx={ContentRowHeader}>
          <Typography variant="h5" component="h1">{title}</Typography>
          <Box sx={ActionsRow}>
            <Button variant="outlined" color="error" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading || !isFormValid}
              onClick={handleSubmit}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>
        </ContentRow>
        {serverError && (
          <ContentRow>
            <Alert severity="error" onClose={() => setServerError(null)}>{serverError}</Alert>
          </ContentRow>
        )}
        <ContentRow>
          <Box component="form" sx={ProvinceContainer} onSubmit={handleSubmit} noValidate>
            <Box sx={FormRow}>
              <TextField
                name="name"
                label="State Name"
                value={formData.name}
                onChange={handleFieldChange}
                fullWidth
                required
              />
              <TextField
                name="code"
                label="State Code"
                value={formData.code}
                onChange={handleFieldChange}
                fullWidth
                inputProps={{maxLength: 10}}
              />
            </Box>
            <Box sx={FormRow}>
              <Autocomplete
                options={countries}
                value={selectedCountry}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={handleCountryChange}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Country" required/>
                )}
              />
            </Box>
          </Box>
        </ContentRow>
      </AppContent>
    </>
  );
}

export default ProvinceForm;
